import { Injectable, ViewChild } from '@angular/core';
import { ChartModel } from '../models/chart.model';
import { environment } from 'src/environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ViewModel } from '../models/view.model';
import { UserService } from './user.service';
import { Dealer } from '../models/dealer.model';
import { Group } from '../models/group.model';
import { TimeFrameForm } from '../dto/timeFrameForm';
import * as moment from 'moment';
import { ChartContainer } from '../models/chart-container.model';

export class PushEditForm {
  timeFrame: TimeFrameForm;
  chart: ChartModel;
  groups: Dealer[];
}

@Injectable({
  providedIn: 'root'
})
export class ChartsService {

  constructor(private httpClient: HttpClient, private userService: UserService) { }

  public getUserCharts<T>(duns?: string, childDuns?: string[]): Observable<ChartModel[]> {
    let url = `${environment.apis.thirdParty}/charts`;
    if (duns) url += `?duns=${duns}`;
    if (childDuns) {
      url += '?'
      for (let id of childDuns) {
        url += `childDuns=${encodeURIComponent(id)}&`;
      }
      url = url.slice(0, -1) // remove last &
    }
    return this.httpClient.get<ChartModel[]>(url);
  }

  public getChartsForExternal(country: string, bac: string): Observable<ChartModel[]> {
    let url = `${environment.apis.thirdParty}/charts/forExternal/${encodeURIComponent(country)}/${encodeURIComponent(bac)}`;
    return this.httpClient.get<ChartModel[]>(url);
  }

  public getChart<T>(guid: string): Observable<T> {
    return this.httpClient.get<T>(`${environment.apis.thirdParty}/charts/${guid}`);
  }

  public getChartsPushedByUser(): Observable<ChartModel[]> {
    return this.httpClient.get<ChartModel[]>(`${environment.apis.thirdParty}/charts/pushedBy`);
  }

  public getDealersPushedToByUserByRegion() {
    return this.httpClient.get(`${environment.apis.thirdParty}/charts/pushedBy/byRegion`);
  }

  public viewChart(chartId: number) {
    return this.httpClient.post(`${environment.apis.thirdParty}/views/chart/${chartId}`, {});
  }

  public searchCharts(query: string, tags: string[]) {
    return this.httpClient.post(`${environment.apis.thirdParty}/charts/search`, { "query": query, "tags": tags });
  }

  public getTags() {
    return this.httpClient.get(`${environment.apis.thirdParty}/charts/tags`);
  }

  public pushCharts(charts: ChartModel[], externalIds: string[]) {
    return this.httpClient.post(`${environment.apis.thirdParty}/charts`, { "charts": charts, "externalIds": externalIds });
  }

  public getGroupsForChart(chartId, timeFrameId?) {
    let query = '';
    if (timeFrameId) query += (timeFrameId) ? `?timeFrameId=${timeFrameId}` : '';
    return this.httpClient.get(`${environment.apis.thirdParty}/charts/${chartId}/groups${query}`);
  }

  public removeGroupsFromChart(insightAccessGuid : string) {
    return this.httpClient.post(`${environment.apis.thirdParty}/charts/security/remove?insightAccessGuid=${insightAccessGuid}`, {})
  }

  public getChartContainers(query? : string) : Observable<ChartContainer[]> {
    if (query) query = `?query=${query}`;
    else query = '';
    return this.httpClient.get<ChartContainer[]>(`${environment.apis.thirdParty}/chart-containers/search${query}`);
  }

  public createChartContainer(chartContainer : ChartContainer) {
    return this.httpClient.post(`${environment.apis.thirdParty}/chart-containers`, chartContainer);
  }

  dayOfWeekMapper = new Map([
    [0, 'Sunday'],
    [1, 'Monday'],
    [2, 'Tuesday'],
    [3, 'Wednesday'],
    [4, 'Thursday'],
    [5, 'Friday'],
    [6, 'Saturday']
  ]);

  public ordinalSuffixOf(i) {
    var j = i % 10,
      k = i % 100;
    if (j == 1 && k != 11) {
      return i + "st";
    }
    if (j == 2 && k != 12) {
      return i + "nd";
    }
    if (j == 3 && k != 13) {
      return i + "rd";
    }
    return i + "th";
  }

  public formatTimeFrame(tf: TimeFrameForm): string {
    var final = '';
    if (tf.repeatDuration == 'Daily') {
      let isRepeatInterval1 = (tf.repeatInterval == 1) ? 'day' : tf.repeatInterval + ' days';
      final += `every ${isRepeatInterval1} starting on ${moment(tf.startDate).format("dddd, MMMM Do YYYY")}`;
    } else if (tf.repeatDuration == 'Weekly') {
      let isRepeatInterval1 = (tf.repeatInterval == 1) ? `week on ${this.dayOfWeekMapper.get(tf.repeatDayOfWeek)}` : `${tf.repeatInterval} weeks on ${this.dayOfWeekMapper.get(tf.repeatDayOfWeek)}`;
      final += `every ${isRepeatInterval1} starting on ${moment(tf.startDate).format("dddd, MMMM Do YYYY")}`;
    } else if (tf.repeatDuration == 'Monthly') {
      let isRepeatInterval1 = (tf.repeatInterval == 1) ? 'month' : tf.repeatInterval + ' months';
      if (tf.monthRepeatType == 0) {
        final += `every ${isRepeatInterval1} on the ${this.ordinalSuffixOf(tf.repeatDayOfMonth)} day of the month`
      } else {
        final += `the ${this.ordinalSuffixOf(tf.repeatWeekOfMonth)} ${this.dayOfWeekMapper.get(tf.repeatDayOfWeek)} every ${isRepeatInterval1}`
      }
      final += ` starting on ${moment(tf.startDate).format("dddd, MMMM Do YYYY")}`;
    } else if (tf.repeatDuration == 'Yearly') {
      let isRepeatInterval1 = (tf.repeatInterval == 1) ? 'year' : tf.repeatInterval + ' years';
      final += `every ${isRepeatInterval1} starting on ${moment(tf.startDate).format("dddd, MMMM Do YYYY")}`;
    }
    if (tf.endDate) final += ` until ${moment(tf.endDate).format("dddd, MMMM Do YYYY")}`
    return final;
  }
}
